import { AuthLayout } from '@/components/AuthLayout'
import { describeAuthError, errorCode, signOutReason } from '@/lib/authErrors'
import { createHonoClient } from '@/stores/api'
import { getSessionToken, getTenant, setSessionToken, setTenant } from '@/stores/session'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@repo/ui/components'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { AlertCircle, ArrowLeft, Building2, Loader2, Mail, ShieldCheck } from 'lucide-react'
import { useRef, useState } from 'react'
import { MESSAGE } from '@repo/constants'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

// Never `/${tenant.slug}/quotes`: the session tenant is the COMPANY, and the
// slug in that path means an OFFICE, so it would resolve to an office the
// company does not buy from and render an empty list (PET-2999).
const QUOTES_PATH = '/quotes'

const loginSearchSchema = z.object({
  redirect: z.string().optional(),
  email: z.string().email().optional(),
  reason: z.string().optional().transform(signOutReason).catch(undefined),
})

export const Route = createFileRoute('/login')({
  validateSearch: loginSearchSchema,
  beforeLoad: ({ search }) => {
    const token = getSessionToken()
    const tenant = getTenant()
    if (token && tenant) {
      const safeRedirect =
        search.redirect && search.redirect.startsWith('/') && !search.redirect.startsWith('//')
          ? search.redirect
          : QUOTES_PATH
      throw redirect({ to: safeRedirect })
    }
  },
  component: LoginPage,
})

type TenantOption = { id: string; name: string; slug: string }
type Step = 'email' | 'code' | 'tenant'

// Unauthenticated client for login calls
const api = createHonoClient(null)

function FormError({ message }: { message: string | null }) {
  if (!message) return null
  return (
    <div className="flex items-center gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">
      <AlertCircle className="size-4 shrink-0" />
      {message}
    </div>
  )
}

function LoginPage() {
  const { t } = useTranslation('auth')
  const { redirect: redirectTo, email: emailFromUrl, reason } = Route.useSearch()
  const navigate = useNavigate()
  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState(emailFromUrl ?? '')
  const [otp, setOtp] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(
    reason ? describeAuthError(t, reason, t('login.errors.sessionEnded')) : null,
  )
  const otpInputRef = useRef<HTMLInputElement>(null)

  // Multi-tenant state
  const [tenants, setTenants] = useState<TenantOption[]>([])
  const [tempToken, setTempToken] = useState<string | null>(null)

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    try {
      const res = await api['portal-auth'].contractor.login.$post({ json: { email } })

      if (!res.ok) {
        const code = errorCode(await res.json().catch(() => null))
        if (res.status === 404) {
          setError(t('login.errors.accountNotFound'))
        } else {
          setError(describeAuthError(t, code, code ?? t('login.errors.generic')))
        }
        return
      }

      setStep('code')
      setTimeout(() => otpInputRef.current?.focus(), 100)
    } catch {
      setError(t('login.errors.generic'))
    } finally {
      setIsLoading(false)
    }
  }

  const completeLogin = (sessionToken: string, tenant: TenantOption) => {
    setSessionToken(sessionToken)
    setTenant(tenant)
    const safeRedirect =
      redirectTo && redirectTo.startsWith('/') && !redirectTo.startsWith('//')
        ? redirectTo
        : QUOTES_PATH
    navigate({ to: safeRedirect })
  }

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    try {
      const res = await api['portal-auth'].contractor['verify-code'].$post({
        json: { email, otp },
      })

      if (!res.ok) {
        const code = errorCode(await res.json().catch(() => null))
        if (code === MESSAGE.TENANT_INACTIVE) {
          setError(describeAuthError(t, code, t('login.errors.verifyFailed')))
        } else if (res.status === 401) {
          setError(t('login.errors.invalidCode'))
        } else if (res.status === 403) {
          setError(t('login.errors.profileNotVerified'))
        } else {
          setError(describeAuthError(t, code, code ?? t('login.errors.verifyFailed')))
        }
        return
      }

      const data = await res.json()

      if ('sessionToken' in data) {
        // Single tenant — login immediately
        completeLogin(data.sessionToken, data.tenant)
      } else if ('requireTenantSelection' in data) {
        setTempToken(data.tempToken)

        // If the redirect URL contains a tenant slug, auto-select that tenant
        const redirectSegments = redirectTo?.split('/').filter(Boolean)
        const redirectSlug = redirectSegments?.[0]
        const matchingTenant = redirectSlug
          ? data.tenants.find((tenant) => tenant.slug === redirectSlug)
          : null

        if (matchingTenant) {
          handleSelectTenantDirect(data.tempToken, matchingTenant.id)
        } else {
          setTenants(data.tenants)
          setStep('tenant')
        }
      }
    } catch {
      setError(t('login.errors.verifyFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleSelectTenantDirect = async (token: string, tenantId: string) => {
    setIsLoading(true)
    setError(null)

    try {
      const res = await api['portal-auth'].contractor['select-tenant'].$post({
        json: { tempToken: token, tenantId },
      })

      if (!res.ok) {
        const code = errorCode(await res.json().catch(() => null))
        setError(describeAuthError(t, code, code ?? t('login.errors.selectTenantFailed')))
        return
      }

      const data = await res.json()
      completeLogin(data.sessionToken, data.tenant)
    } catch {
      setError(t('login.errors.selectTenantFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleSelectTenant = (tenantId: string) => {
    if (!tempToken) return
    handleSelectTenantDirect(tempToken, tenantId)
  }

  const handleResendCode = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const res = await api['portal-auth'].contractor.login.$post({ json: { email } })

      if (!res.ok) {
        setError(t('login.errors.resendFailed'))
        return
      }

      setOtp('')
      setError(null)
    } catch {
      setError(t('login.errors.resendFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  const getDescription = () => {
    if (step === 'email') return t('login.email.description')
    if (step === 'code') return t('login.code.description', { email })
    return t('login.tenant.description')
  }

  return (
    <AuthLayout>
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">
            {step === 'tenant' ? t('login.tenant.title') : t('login.title')}
          </CardTitle>
          <CardDescription>{getDescription()}</CardDescription>
          {step === 'code' && (
            <p className="mt-1 text-xs text-muted-foreground">{t('login.code.spamHint')}</p>
          )}
        </CardHeader>
        <CardContent>
          {step === 'email' && (
            <form onSubmit={handleSendCode} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('login.email.label')}</Label>
                <div className="relative">
                  <Mail className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('login.email.placeholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9"
                    required
                  />
                </div>
              </div>

              <FormError message={error} />

              <Button type="submit" className="w-full" isDisabled={isLoading || !email}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    {t('login.email.sending')}
                  </>
                ) : (
                  t('login.email.submit')
                )}
              </Button>
            </form>
          )}

          {step === 'code' && (
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="otp">{t('login.code.label')}</Label>
                <div className="relative">
                  <ShieldCheck className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    ref={otpInputRef}
                    id="otp"
                    type="text"
                    inputMode="numeric"
                    placeholder={t('login.code.placeholder')}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className="pl-9"
                    autoComplete="one-time-code"
                    required
                  />
                </div>
              </div>

              <FormError message={error} />

              <Button type="submit" className="w-full" isDisabled={isLoading || !otp}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    {t('login.code.verifying')}
                  </>
                ) : (
                  t('login.code.submit')
                )}
              </Button>

              <div className="flex items-center justify-between text-sm">
                <Button
                  variant="ghost"
                  size="sm"
                  onPress={() => {
                    setStep('email')
                    setOtp('')
                    setError(null)
                  }}
                >
                  <ArrowLeft className="size-3" />
                  {t('login.code.changeEmail')}
                </Button>
                <Button variant="link" size="sm" onPress={handleResendCode} isDisabled={isLoading}>
                  {t('login.code.resend')}
                </Button>
              </div>
            </form>
          )}

          {step === 'tenant' && (
            <div className="space-y-4">
              <div className="space-y-2">
                {tenants.map((tenant) => (
                  <Button
                    key={tenant.id}
                    variant="outline"
                    className="h-auto w-full justify-start gap-3 p-3"
                    onPress={() => handleSelectTenant(tenant.id)}
                    isDisabled={isLoading}
                  >
                    <div className="flex size-10 items-center justify-center rounded-lg bg-muted">
                      <Building2 className="size-5 text-muted-foreground" />
                    </div>
                    <span className="text-sm font-medium">{tenant.name}</span>
                  </Button>
                ))}
              </div>

              <FormError message={error} />

              <Button
                variant="ghost"
                size="sm"
                onPress={() => {
                  setStep('email')
                  setOtp('')
                  setTenants([])
                  setTempToken(null)
                  setError(null)
                }}
              >
                <ArrowLeft className="size-3" />
                {t('login.tenant.startOver')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
